import React, { useState, useEffect, FormEvent } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe';
import { createPaymentIntent } from '@/service/payment';
import { getUserCurrentBalance } from '@/service/payment';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Lock, RotateCcw, Loader2 } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import useAuth from '@/auth/useAuth';

// Product configurations for different currencies
const TOPUP_PRODUCTS = {
  usd: {
    id: 'prod_U06AQCAf9H1ovc',
    amount: 5.00,
    currency: 'usd',
    symbol: '$',
    label: 'USD $5.00',
    flag: '🇺🇸',
    description: 'International payment',
  },
  thb: {
    id: 'prod_U08LpjwXupe8n6',
    amount: 150,
    currency: 'thb',
    symbol: '฿',
    label: 'THB 150฿',
    flag: '🇹🇭',
    description: 'Thai payment with PromptPay',
  },
} as const;

type CurrencyOption = keyof typeof TOPUP_PRODUCTS;

// Helper to detect if user is in Thailand (for default selection)
function detectDefaultCurrency(): CurrencyOption {
  // Check URL parameter for testing: ?currency=thb
  const urlParams = new URLSearchParams(window.location.search);
  const urlCurrency = urlParams.get('currency');
  if (urlCurrency === 'thb' || urlCurrency === 'usd') {
    console.log(`[QuickTopup] Forcing ${urlCurrency.toUpperCase()} via URL parameter`);
    return urlCurrency as CurrencyOption;
  }

  // Auto-detect based on timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = navigator.language || '';

  const isThailand = timezone === 'Asia/Bangkok' ||
                    timezone === 'Asia/Phnom_Penh' ||
                    timezone === 'Asia/Vientiane' ||
                    locale.startsWith('th');

  console.log('[QuickTopup] Timezone:', timezone);
  console.log('[QuickTopup] Locale:', locale);
  console.log('[QuickTopup] Detected currency:', isThailand ? 'THB' : 'USD');

  return isThailand ? 'thb' : 'usd';
}

interface QuickTopupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface CheckoutFormProps {
  clientSecret: string;
  amount: number;
  currency: string;
  onSuccess: () => void;
  onCancel: () => void;
  initialBalance: number;
  userId?: string;
  productId: string;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  amount,
  currency,
  onSuccess,
  onCancel,
  initialBalance,
  userId,
  productId,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isWaitingForWebhook, setIsWaitingForWebhook] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [paymentStartTime] = useState(Date.now());

  // Poll for balance update after payment confirmation
  const pollBalanceUpdate = async (startBalance: number) => {
    const maxAttempts = 30; // 30 seconds max
    const pollInterval = 1000; // Check every 1 second

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        await new Promise(resolve => setTimeout(resolve, pollInterval));

        const response = await getUserCurrentBalance();
        const newBalance = response.balance;

        // Check if balance has increased
        if (newBalance > startBalance) {
          return true;
        }
      } catch (error) {
        console.error('Error polling balance:', error);
      }
    }

    return false; // Timeout
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Track payment submission
    trackEvent('payment', 'payment_submit_clicked', {
      user_id: userId,
      metadata: {
        product_id: productId,
        amount: amount / 100,
        currency,
        initial_balance: initialBalance,
        page_path: window.location.pathname,
      },
    });

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        // Track payment error
        const errorDuration = Date.now() - paymentStartTime;
        trackEvent('payment', 'payment_failed', {
          user_id: userId,
          duration_ms: errorDuration,
          metadata: {
            product_id: productId,
            amount: amount / 100,
            currency,
            error_type: error.type,
            error_code: error.code,
            error_message: error.message,
            page_path: window.location.pathname,
          },
        });

        setErrorMessage(error.message || 'An unexpected error occurred.');
        setIsProcessing(false);
      } else {
        // Track successful payment confirmation
        const confirmDuration = Date.now() - paymentStartTime;
        trackEvent('payment', 'payment_confirmed', {
          user_id: userId,
          duration_ms: confirmDuration,
          metadata: {
            product_id: productId,
            amount: amount / 100,
            currency,
            page_path: window.location.pathname,
          },
        });

        // Payment confirmed by Stripe, now wait for webhook to update balance
        setIsProcessing(false);
        setIsWaitingForWebhook(true);

        const webhookStartTime = Date.now();
        const balanceUpdated = await pollBalanceUpdate(initialBalance);

        if (balanceUpdated) {
          // Track successful balance update
          const webhookDuration = Date.now() - webhookStartTime;
          const totalDuration = Date.now() - paymentStartTime;

          trackEvent('payment', 'payment_completed', {
            user_id: userId,
            duration_ms: totalDuration,
            metadata: {
              product_id: productId,
              amount: amount / 100,
              currency,
              webhook_duration_ms: webhookDuration,
              confirm_duration_ms: confirmDuration,
              new_balance: initialBalance + (amount / 100),
              balance_increase: amount / 100,
              page_path: window.location.pathname,
            },
          });

          onSuccess();
        } else {
          // Track webhook timeout
          const webhookDuration = Date.now() - webhookStartTime;
          trackEvent('payment', 'payment_webhook_timeout', {
            user_id: userId,
            duration_ms: webhookDuration,
            metadata: {
              product_id: productId,
              amount: amount / 100,
              currency,
              timeout_seconds: 30,
              page_path: window.location.pathname,
            },
          });

          // Balance not updated yet, but payment succeeded
          // Still call onSuccess to close dialog and show success message
          // The balance will update eventually
          onSuccess();
        }
      }
    } catch (err) {
      // Track unexpected error
      const errorDuration = Date.now() - paymentStartTime;
      trackEvent('payment', 'payment_exception', {
        user_id: userId,
        duration_ms: errorDuration,
        metadata: {
          product_id: productId,
          amount: amount / 100,
          currency,
          error_message: err instanceof Error ? err.message : 'Unknown error',
          page_path: window.location.pathname,
        },
      });

      setErrorMessage('Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Amount Display */}
      <div className="bg-muted/50 rounded-lg p-4 border">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Top-up Amount</span>
          <span className="text-2xl font-bold text-foreground">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: currency.toUpperCase(),
            }).format(amount / 100)}
          </span>
        </div>
      </div>

      {/* Payment Element */}
      <PaymentElement
        options={{
          wallets: {
            applePay: 'auto',
            googlePay: 'auto',
          },
        }}
      />

      {/* Payment Methods Info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <svg className="h-5 w-5 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0z" fill="#0073E6"/>
            <path d="M7.5 10.5l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">
              💳 Multiple payment options
            </p>
            <p className="text-xs text-blue-700 mt-1">
              {currency === 'thb' ? (
                <>Cards • PromptPay (Thai QR) • Link</>
              ) : (
                <>Cards • Apple Pay (Safari) • Google Pay (Chrome) • Link</>
              )}
            </p>
            {/* Debug Info */}
            <details className="mt-2">
              <summary className="text-xs text-blue-600 cursor-pointer hover:underline">
                Not seeing payment options? Click for help
              </summary>
              <div className="mt-2 p-2 bg-white rounded border border-blue-200 text-xs space-y-1">
                <p className="font-medium text-gray-900">Current Setup:</p>
                <p className="text-gray-600">• Currency: <span className="font-mono">{currency.toUpperCase()}</span></p>
                <p className="text-gray-600">• Browser: <span className="font-mono">{navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome') ? 'Safari' : navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Other'}</span></p>
                <p className="text-gray-600">• Device: <span className="font-mono">{navigator.platform}</span></p>
                <div className="mt-2 pt-2 border-t border-blue-100">
                  <p className="font-medium text-gray-900 mb-1">Why methods might not show:</p>
                  <ul className="list-disc list-inside text-gray-600 space-y-0.5">
                    <li>Apple Pay: Only on Safari with Apple Wallet configured</li>
                    <li>Google Pay: Only on Chrome with Google Pay account</li>
                    <li>PromptPay: Only when currency is THB (test with ?currency=thb)</li>
                  </ul>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="space-y-3 pt-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lock className="h-4 w-4 text-green-600" />
          <span>Secure payment powered by Stripe</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-blue-600" />
          <span>Your payment information is encrypted and secure</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <RotateCcw className="h-4 w-4 text-orange-600" />
          <span className="font-medium text-foreground">
            100% Money-Back Guarantee
          </span>
        </div>
        <p className="text-xs text-muted-foreground pl-6">
          If our service doesn't work as expected, we'll refund your payment—no questions asked.
        </p>
      </div>

      {errorMessage && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md border border-destructive/20">
          {errorMessage}
        </div>
      )}

      {isWaitingForWebhook && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Payment successful! Processing your top-up...
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Please wait while we update your balance. This usually takes a few seconds.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing || isWaitingForWebhook}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || isProcessing || isWaitingForWebhook}
          className="flex-1"
        >
          {isProcessing ? 'Processing Payment...' : isWaitingForWebhook ? 'Updating Balance...' : 'Pay Now'}
        </Button>
      </div>

      {/* Stripe Badge */}
      <div className="flex items-center justify-center gap-2 pt-2 pb-1">
        <span className="text-xs text-muted-foreground">Powered by</span>
        <svg className="h-4 w-auto" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
          <path fill="var(--stripe-color, #635BFF)" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z"/>
        </svg>
      </div>
    </form>
  );
};

export const QuickTopupDialog: React.FC<QuickTopupDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyOption>(detectDefaultCurrency());
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [currency, setCurrency] = useState<string>('usd');
  const [initialBalance, setInitialBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productId, setProductId] = useState<string>('');
  const [dialogOpenTime, setDialogOpenTime] = useState<number>(0);

  // Reset payment intent when currency changes
  const handleCurrencyChange = (newCurrency: CurrencyOption) => {
    if (newCurrency !== selectedCurrency) {
      // Track currency change
      trackEvent('payment', 'currency_changed', {
        user_id: userId,
        metadata: {
          from_currency: selectedCurrency,
          to_currency: newCurrency,
          page_path: window.location.pathname,
        },
      });

      setSelectedCurrency(newCurrency);
      // Reset payment intent to fetch new one
      setClientSecret(null);
      setError(null);
    }
  };

  useEffect(() => {
    if (open && !clientSecret) {
      const openTime = Date.now();
      if (dialogOpenTime === 0) {
        setDialogOpenTime(openTime);
      }
      setIsLoading(true);
      setError(null);

      // Get product configuration based on selected currency
      const productConfig = TOPUP_PRODUCTS[selectedCurrency];
      const selectedProductId = productConfig.id;
      setProductId(selectedProductId);
      console.log('[QuickTopup] Using product:', selectedProductId, `(${selectedCurrency.toUpperCase()})`);

      // Track dialog opened
      trackEvent('payment', 'topup_dialog_opened', {
        user_id: userId,
        metadata: {
          product_id: selectedProductId,
          page_path: window.location.pathname,
          trigger: 'insufficient_balance',
        },
      });

      // Fetch both payment intent and current balance
      Promise.all([
        createPaymentIntent({ product_id: selectedProductId }),
        getUserCurrentBalance()
      ])
        .then(([paymentResponse, balanceResponse]) => {
          const loadDuration = Date.now() - openTime;
          console.log('[QuickTopup] Payment response:', paymentResponse);
          setClientSecret(paymentResponse.client_secret);
          setAmount(paymentResponse.amount);
          setCurrency(paymentResponse.currency);
          setInitialBalance(balanceResponse.balance);

          // Track successful payment intent creation
          trackEvent('payment', 'payment_intent_created', {
            user_id: userId,
            duration_ms: loadDuration,
            metadata: {
              product_id: selectedProductId,
              amount: paymentResponse.amount / 100,
              currency: paymentResponse.currency,
              current_balance: balanceResponse.balance,
              page_path: window.location.pathname,
            },
          });

          // Log what payment methods will be available
          if (paymentResponse.currency === 'thb') {
            console.log('[QuickTopup] ✅ THB currency - PromptPay will be available!');
          } else {
            console.log('[QuickTopup] Currency is', paymentResponse.currency, '- PromptPay not available');
          }
        })
        .catch((err) => {
          const errorDuration = Date.now() - openTime;
          console.error('Failed to initialize payment:', err);

          // Track error
          trackEvent('payment', 'payment_intent_failed', {
            user_id: userId,
            duration_ms: errorDuration,
            metadata: {
              product_id: selectedProductId,
              error_message: err instanceof Error ? err.message : 'Unknown error',
              page_path: window.location.pathname,
            },
          });

          setError('Failed to initialize payment. Please try again.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [open, clientSecret, userId]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Track dialog closed
      const timeOpen = dialogOpenTime > 0 ? Date.now() - dialogOpenTime : 0;
      trackEvent('payment', 'topup_dialog_closed', {
        user_id: userId,
        duration_ms: timeOpen,
        metadata: {
          product_id: productId,
          amount: amount / 100,
          currency,
          payment_completed: false, // Closed without payment
          page_path: window.location.pathname,
        },
      });

      // Reset state when closing
      setClientSecret(null);
      setAmount(0);
      setCurrency('usd');
      setInitialBalance(0);
      setError(null);
      setDialogOpenTime(0);
    }
    onOpenChange(newOpen);
  };

  const stripePromise = getStripe();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[540px] max-h-[90vh] flex flex-col">
        <DialogHeader className="space-y-1 flex-shrink-0">
          <DialogTitle className="text-2xl">Quick Top-up</DialogTitle>
          <DialogDescription>
            Add credits to your account to continue using our services
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-4">
          {/* Currency Selector */}
          <div className="space-y-2">
          <label className="text-sm font-medium">Select Currency</label>
          <div className="grid grid-cols-2 gap-3">
            {(Object.keys(TOPUP_PRODUCTS) as CurrencyOption[]).map((curr) => {
              const config = TOPUP_PRODUCTS[curr];
              const isSelected = selectedCurrency === curr;

              return (
                <button
                  key={curr}
                  type="button"
                  onClick={() => handleCurrencyChange(curr)}
                  disabled={isLoading}
                  className={`
                    relative flex flex-col items-start p-4 rounded-lg border-2 transition-all
                    ${isSelected
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                    }
                    ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {/* Selection indicator */}
                  <div className={`
                    absolute top-2 right-2 w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${isSelected ? 'border-primary bg-primary' : 'border-gray-300 bg-white'}
                  `}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>

                  {/* Currency info */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{config.flag}</span>
                    <span className={`font-semibold ${isSelected ? 'text-primary' : 'text-gray-900'}`}>
                      {config.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground text-left">
                    {config.description}
                  </p>

                  {/* Payment methods hint */}
                  {curr === 'thb' && (
                    <div className="mt-2 text-xs text-blue-600 font-medium">
                      ✓ Includes PromptPay QR
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          </div>

          {isLoading && (
            <div className="py-12 text-center">
              <div className="inline-flex items-center gap-3">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                <span className="text-sm text-muted-foreground">
                  Initializing secure payment...
                </span>
              </div>
            </div>
          )}

          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-4 rounded-md border border-destructive/20">
              <p className="font-medium">Payment initialization failed</p>
              <p className="mt-1 text-xs">{error}</p>
            </div>
          )}

          {!isLoading && !error && clientSecret && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#0F172A',
                    colorBackground: '#ffffff',
                    colorText: '#0F172A',
                    colorDanger: '#df1b41',
                    fontFamily: 'system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '6px',
                  },
                },
                // Enable Stripe Link for faster checkout
                loader: 'auto',
              }}
            >
              <CheckoutForm
                clientSecret={clientSecret}
                amount={amount}
                currency={currency}
                onSuccess={onSuccess}
                onCancel={() => handleOpenChange(false)}
                initialBalance={initialBalance}
                userId={userId}
                productId={productId}
              />
            </Elements>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
